import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Card, Typography, IconButton, Tooltip, Chip,
  TextField, InputAdornment, Button, CircularProgress, Stack,
  TablePagination, useTheme, useMediaQuery, Popover
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import { PageHeader } from "../../components/PageHeader.js";
import { EmptyState } from "../../components/EmptyState.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { DataGridRowActionsMenu } from "../../components/DataGridRowActionsMenu.js";
import { QueueStatusChip } from "../../components/StatusChip.js";
import { useApi } from "../../hooks/useApi.js";
import { useDispatchJob, type DispatchResult } from "../../hooks/useDispatchJob.js";
import { useWhatsappEvents } from "../../hooks/useWhatsappEvents.js";
import { ui } from "../../store/uiStore.js";
import { whatsappErrorLabel } from "../../lib/whatsappErrors.js";
import { formLabel } from "../../lib/labels.js";
import { useTranslation } from "react-i18next";
import type { WhatsAppQueue } from "@kappa-v2/contracts";
import { format } from "date-fns";

// Allineato a produzione: una coda PER destinatario (fullName/cellPhone/attachedType/status).
const shortStatus = (s?: string) => s?.split(".").pop() ?? "";

export function WhatsAppQueuesPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState<WhatsAppQueue[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppQueue | null>(null);
  const [requeuing, setRequeuing] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [errPop, setErrPop] = useState<{ el: HTMLElement; msg: string } | null>(null);

  const fetchAll = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    call<{ data: WhatsAppQueue[] } | WhatsAppQueue[]>("/api/v1/whatsapp/queues", silent ? { silent: true, noLoader: true } : {})
      .then(r => setRows(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => { if (!silent) setRows([]); })
      .finally(() => { if (!silent) setLoading(false); });
  }, [call]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Aggiornamento in tempo reale alle modifiche delle code (debounced, silenzioso).
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useWhatsappEvents((kind) => {
    if (kind !== "queues") return;
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => fetchAll(true), 600);
  });

  // Stato del job di invio (push SSE): aggiorna la griglia e avvisa a fine job.
  const onJobFinished = useCallback((r?: DispatchResult) => {
    fetchAll();
    if ((r?.processed ?? 0) > 0) {
      ui.toast(t("whatsapp.queues.dispatched", { sent: r?.sent ?? 0, failed: r?.failed ?? 0, blocked: r?.blocked ?? 0 }), "success");
    }
  }, [fetchAll, t]);
  const job = useDispatchJob(onJobFinished);
  const [launching, setLaunching] = useState(false);
  const running = job.running || launching;

  const launchJob = async () => {
    setLaunching(true);
    try {
      await call("/api/v1/whatsapp/queues/dispatch", { method: "POST", body: { limit: 200 }, silent: true });
    } catch (e) {
      const status = (e as { status?: number })?.status;
      ui.toast(status === 409 ? t("whatsapp.queues.alreadyRunning") : t("common.error"), status === 409 ? "info" : "error");
    } finally {
      setLaunching(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/whatsapp/queues/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null); fetchAll();
  };

  const requeue = async (q: WhatsAppQueue) => {
    const id = String(q._id);
    if (!id) return;
    setRequeuing((s) => new Set(s).add(id));
    try {
      const r = await call<{ sent?: number; running?: boolean }>(
        `/api/v1/whatsapp/queues/${id}/resend`, { method: "POST", silent: true }
      );
      ui.toast(r?.running ? t("whatsapp.queues.requeueRunning") : t("whatsapp.queues.requeued", { sent: r?.sent ?? 0 }), r?.running ? "info" : "success");
      fetchAll();
    } catch {
      ui.toast(t("common.error"), "error");
    } finally {
      setRequeuing((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const buildActions = (q: WhatsAppQueue) => [
    { key: "requeue", label: t("whatsapp.queues.requeueAction"), description: t("whatsapp.queues.requeueActionDesc"), icon: <ReplayIcon fontSize="small" />, disabled: requeuing.has(String(q._id)), onClick: () => void requeue(q) },
    { key: "delete", label: t("whatsapp.queues.deleteAction"), description: t("whatsapp.queues.deleteActionDesc"), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteTarget(q) }
  ];

  const filtered = rows.filter(r =>
    `${r.fullName ?? ""} ${r.cellPhone ?? ""} ${r.message ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  useEffect(() => { setPage(0); }, [search]);

  // Click su una coda "Fallita": mostra il motivo. Usa l'errore salvato sulla coda
  // (fallimenti recenti) oppure, per le code vecchie, lo recupera dai messaggi falliti.
  const showQueueError = useCallback(async (el: HTMLElement, q: WhatsAppQueue) => {
    if (q.error) { setErrPop({ el, msg: whatsappErrorLabel(q.error, t) }); return; }
    setErrPop({ el, msg: "…" });
    try {
      const r = await call<{ data?: Array<{ error?: string | null }> } | Array<{ error?: string | null }>>(
        `/api/v1/whatsapp/messages?queueId=${q._id}&status=form.whatsapp.failed&limit=20`, { silent: true, noLoader: true }
      );
      const data = Array.isArray(r) ? r : (r.data ?? []);
      const errs = [...new Set(data.map((m) => m.error).filter(Boolean))] as string[];
      const labeled = errs.map((e) => whatsappErrorLabel(e, t));
      setErrPop({ el, msg: labeled.length ? labeled.join("\n") : t("whatsapp.messages.noFailureReason") });
    } catch {
      setErrPop({ el, msg: t("whatsapp.messages.noFailureReason") });
    }
  }, [call, t]);

  const columns: GridColDef[] = [
    {
      field: "fullName", headerName: t("whatsapp.queues.recipient"), flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams<WhatsAppQueue>) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{params.row.fullName}</Typography>
          <Typography variant="caption" color="text.secondary">{params.row.cellPhone}</Typography>
        </Box>
      )
    },
    {
      field: "status", headerName: t("common.status"), width: 130,
      renderCell: (params: GridRenderCellParams<WhatsAppQueue>) =>
        params.row.status === "form.whatsapp.failed" ? (
          <span
            role="button"
            style={{ cursor: "pointer" }}
            title={t("whatsapp.messages.failureReason")}
            onClick={(e) => void showQueueError(e.currentTarget, params.row)}
          >
            <QueueStatusChip status={shortStatus(params.row.status)} />
          </span>
        ) : (
          <QueueStatusChip status={shortStatus(params.row.status)} />
        )
    },
    {
      field: "attachedType", headerName: t("whatsapp.queues.attachment"), width: 170,
      renderCell: (params: GridRenderCellParams<WhatsAppQueue>) => (
        <Chip size="small" variant="outlined" label={formLabel(params.row.attachedType)} sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "createdAt", headerName: t("whatsapp.queues.createdAt"), width: 150,
      renderCell: (params: GridRenderCellParams<WhatsAppQueue>) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.createdAt ? format(new Date(params.row.createdAt), "dd/MM/yyyy HH:mm") : "—"}
        </Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 56, sortable: false, align: "center", headerAlign: "center",
      renderCell: (params: GridRenderCellParams<WhatsAppQueue>) => (
        <DataGridRowActionsMenu ariaLabel={t("common.actions")} items={buildActions(params.row)} />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("whatsapp.queues.title")}
        subtitle={t("whatsapp.queues.subtitle")}
        breadcrumbs={[{ label: t("sidebar.groupCommunications") }, { label: t("sidebar.queuesWa") }]}
      />
      <Box sx={{ p: 3, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <TextField size="small" placeholder={t("whatsapp.queues.searchPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")} edge="end" aria-label="clear">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{ width: { xs: "100%", sm: 380 }, "& .MuiInputBase-root": { fontSize: "1rem" } }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t("common.refresh")}>
                <IconButton size="small" onClick={() => fetchAll()} aria-label="refresh">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                data-tour="wa-launch"
                variant="contained" color="success"
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                disabled={running}
                onClick={launchJob}
              >
                {running ? t("whatsapp.queues.running") : t("whatsapp.queues.launchJob")}
              </Button>
            </Stack>
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("whatsapp.queues.none")} description={t("whatsapp.queues.noneDesc")} />
            : isMobile
              ? (
                <Box>
                  {paged.map((r, idx) => (
                    <Box key={r._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700}>{r.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{r.cellPhone}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                          <QueueStatusChip status={shortStatus(r.status)} />
                          <Chip size="small" variant="outlined" label={formLabel(r.attachedType)} sx={{ fontSize: "0.72rem" }} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {r.createdAt ? format(new Date(r.createdAt), "dd/MM/yyyy HH:mm") : "—"}
                        </Typography>
                      </Box>
                      <DataGridRowActionsMenu ariaLabel={t("common.actions")} items={buildActions(r)} />
                    </Box>
                  ))}
                  <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage={t("whatsapp.queues.rowsPerPage")}
                    sx={{ borderTop: "1px solid rgba(0,0,0,.08)" }}
                  />
                </Box>
              )
              : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                  loading={loading} autoHeight rowHeight={60} pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  disableRowSelectionOnClick sx={{ border: "none" }}
                />
          }
        </Card>
      </Box>
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("whatsapp.queues.deleteConfirm", { name: deleteTarget?.fullName })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <Popover
        open={Boolean(errPop)}
        anchorEl={errPop?.el}
        onClose={() => setErrPop(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, maxWidth: 360 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: "block", color: "error.main" }}>
            {t("whatsapp.messages.failureReason")}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{errPop?.msg}</Typography>
        </Box>
      </Popover>
    </Box>
  );
}
